import { useMemo } from 'react';
import { config } from '@/config';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { UserRole } from '@/types';
import { ROUTES } from '@/types/routes';
import { Grid } from 'antd';

interface NavItem {
  path: string;
  label: string;
  icon?: string;
  external?: boolean;
}

// TCX-794: Warn (once) instead of throwing at module-eval when an external
// nav link (franchisor TCX Scout, consultant Franchise Feed) is unset — an empty
// value only drops the corresponding nav item rather than crashing the whole
// bundle to a blank screen at boot.
let warnedMissingScoutLink = false;
function warnMissingScoutLink(keys: string[]): void {
  if (warnedMissingScoutLink) return;
  warnedMissingScoutLink = true;
  // eslint-disable-next-line no-console
  console.warn(
    `External nav item hidden: missing env ${keys.join(' and/or ')}`,
  );
}

// A NavItem whose `path` is empty (unset external link) would render a dead
// external link, so it is omitted from the built navigation. TCX-1043 moved
// consultants to Franchise Feed and kept the VITE_*_TCX_SCOUT_LINK env names;
// franchisors still get TCX Scout.
const withScoutItem = (items: NavItem[], scoutItem: NavItem): NavItem[] =>
  scoutItem.path ? [...items, scoutItem] : items;

export const useUserNavigation = (): NavItem[] => {
  const { lg } = Grid.useBreakpoint();
  const user = useAppSelector(selectUser);

  const consultantTcxScoutLink = config.VITE_CONSULTANT_TCX_SCOUT_LINK;
  const franchisorTcxScoutLink = config.VITE_FRANCHISOR_TCX_SCOUT_LINK;

  const missingScoutKeys: string[] = [];
  if (!franchisorTcxScoutLink)
    missingScoutKeys.push('VITE_FRANCHISOR_TCX_SCOUT_LINK');
  if (!consultantTcxScoutLink)
    missingScoutKeys.push('VITE_CONSULTANT_TCX_SCOUT_LINK');
  if (missingScoutKeys.length > 0) warnMissingScoutLink(missingScoutKeys);

  const navigation: Record<UserRole, NavItem[]> = {
    franchisor: withScoutItem(
      [
        // { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: 'dashboard' },
        {
          path: ROUTES.FIND_CONSULTANT,
          label: 'Find Consultants',
          icon: 'search',
        },
        { path: ROUTES.MY_BOOKINGS, label: 'My Meetings', icon: 'calendar' },
        { path: ROUTES.PAYMENTS, label: 'Payments', icon: 'creditCard' },
        { path: ROUTES.CHATS, label: 'Messages', icon: 'chat' },
      ],
      {
        path: franchisorTcxScoutLink,
        label: 'TCX Scout',
        icon: 'stars',
        external: true,
      },
    ),
    consultant: withScoutItem(
      [
        // { path: ROUTES.DASHBOARD, label: 'Dashboard', icon: 'dashboard' },
        {
          path: ROUTES.FIND_FRANCHISORS,
          label: 'Find Franchisors',
          icon: 'search',
        },
        { path: ROUTES.MY_BOOKINGS, label: 'My Meetings', icon: 'calendar' },
        { path: ROUTES.PAYMENTS, label: 'Payments', icon: 'creditCard' },
        { path: ROUTES.CHATS, label: 'Messages', icon: 'chat' },
      ],
      {
        path: consultantTcxScoutLink,
        label: 'Franchise Feed',
        icon: 'stars',
        external: true,
      },
    ),
    admin: [],
    // TCX-666: admin-shell roles have no consumer top-nav (mirrors `admin`).
    superAdmin: [],
  };

  return useMemo(() => {
    if (!user) return [];

    return (navigation[user.role!] || []).filter((item) =>
      lg ? item.path !== ROUTES.MY_PROFILE : true,
    );
  }, [user, navigation]);
};
